import React from "react";
import { PlusCircle, X } from "lucide-react";

const rawMaterialDefaults = {
  rawMaterialId: "",
  quantity: 1,
  area: 0
};

export default function ProductModal({
  isOpen,
  title = "Add Product",
  submitLabel = "Add Product",
  onClose,
  onSubmit,
  productForm,
  setProductForm,
  errors = {},
  categories = [],
  rawMaterials = [],
  rawMaterialAssignment = rawMaterialDefaults,
  setRawMaterialAssignment,
  extraFields = [],
  onAddExtraField,
  onUpdateExtraField,
  onRemoveExtraField,
  onFileChange,
  onCalculateTotalDimension
}) {
  if (!isOpen) return null;

  const assignment = rawMaterialAssignment || rawMaterialDefaults;
  const updateProductField = (field, value) => {
    setProductForm(prev => ({ ...prev, [field]: value }));
  };

  const updateAssignmentField = (field, value) => {
    if (!setRawMaterialAssignment) return;
    setRawMaterialAssignment(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: "900px" }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="modal-close" type="button" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="form-grid">
            <div className="form-group">
              <label>Product Name <span className="required-star">*</span></label>
              <input
                type="text"
                value={productForm.name}
                onChange={(e) => updateProductField("name", e.target.value)}
                className={errors.name ? "error" : ""}
                placeholder="Enter product name"
              />
              {errors.name && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label>Product Code <span className="required-star">*</span></label>
              <input
                type="text"
                value={productForm.code}
                onChange={(e) => updateProductField("code", e.target.value)}
                className={errors.code ? "error" : ""}
                placeholder="Enter product code"
              />
              {errors.code && <span className="error-text">{errors.code}</span>}
            </div>

            <div className="form-group full-width">
              <label>Description <span className="optional-text">(Optional)</span></label>
              <textarea
                rows="2"
                value={productForm.description}
                onChange={(e) => updateProductField("description", e.target.value)}
                placeholder="Enter product description"
              />
            </div>

            <div className="form-group">
              <label>Category <span className="required-star">*</span></label>
              <select
                value={productForm.category}
                onChange={(e) => updateProductField("category", e.target.value)}
                className={errors.category ? "error" : ""}
              >
                <option value="">Select Category</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.name}>{category.name}</option>
                ))}
              </select>
              {errors.category && <span className="error-text">{errors.category}</span>}
            </div>

            <div className="form-group">
              <label>Dimension (sq mm) <span className="optional-text">(Optional)</span></label>
              <input
                type="text"
                value={productForm.dimension}
                onChange={(e) => updateProductField("dimension", e.target.value)}
                placeholder="e.g., 450x150mm"
              />
            </div>

            <div className="form-group">
              <label>Extra Dimension <span className="optional-text">(Optional)</span></label>
              <input
                type="text"
                value={productForm.extraDimension}
                onChange={(e) => updateProductField("extraDimension", e.target.value)}
                onBlur={onCalculateTotalDimension}
                placeholder="Extra dimension"
              />
            </div>

            <div className="form-group">
              <label>Total Dimension <span className="optional-text">(Auto-calculated)</span></label>
              <input type="text" value={productForm.totalDimension} disabled className="auto-field" readOnly />
            </div>

            {[
              "Select Drawing",
              "Select Gauge",
              "Select Tools",
              "Select Instruments",
              "Select Accessories",
              "Select Fixture",
              "Select Raw Material",
              "Select Sub Product"
            ].map((label) => (
              <div key={label} className="form-group">
                <label>{label} <span className="optional-text">(Auto-calculated)</span></label>
                <input type="text" value={productForm.totalDimension} disabled className="auto-field" readOnly />
              </div>
            ))}

            <div className="form-group">
              <label>Cost <span className="required-star">*</span></label>
              <input
                type="number"
                value={productForm.cost}
                onChange={(e) => updateProductField("cost", e.target.value === "" ? "" : parseFloat(e.target.value))}
                className={errors.cost ? "error" : ""}
                placeholder="Enter product cost"
              />
              {errors.cost && <span className="error-text">{errors.cost}</span>}
            </div>

            <div className="form-group">
              <label>Product Image <span className="optional-text">(Optional)</span></label>
              <input type="file" accept="image/*" onChange={(e) => onFileChange("image", e.target.files[0])} />
              {productForm.imagePreview && (
                <div style={{ marginTop: "8px" }}>
                  <img
                    src={productForm.imagePreview}
                    alt="Preview"
                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px" }}
                  />
                </div>
              )}
            </div>

            <div className="form-group">
              <label>3D File (GLTF) <span className="optional-text">(Optional)</span></label>
              <input type="file" accept=".gltf,.glb" onChange={(e) => onFileChange("3d", e.target.files[0])} />
              {productForm._3dPreview && <small>3D file loaded</small>}
            </div>
          </div>

          <div className="long-text-fields-section">
            <h4>Additional Information</h4>
            <div className="form-group full-width">
              <label>Specifications / Notes <span className="optional-text">(Optional)</span></label>
              <textarea rows="3" placeholder="Enter product specifications, notes, or any additional information here..." />
            </div>
          </div>

          <div className="extra-fields-section">
            <label>Add Extra Fields <span className="optional-text">(Optional)</span></label>
            {extraFields.map((field, index) => (
              <div key={index} className="extra-field-row">
                <input
                  type="text"
                  placeholder="Field name"
                  value={field.name}
                  onChange={(e) => onUpdateExtraField(index, "name", e.target.value)}
                  className="extra-field-input"
                />
                <input
                  type="text"
                  placeholder="Field value"
                  value={field.value}
                  onChange={(e) => onUpdateExtraField(index, "value", e.target.value)}
                  className="extra-field-input"
                />
                <button type="button" onClick={() => onRemoveExtraField(index)} className="remove-field-btn">
                  <X size={16} />
                </button>
              </div>
            ))}
            <button type="button" className="add-field-btn" onClick={onAddExtraField}>
              <PlusCircle size={14} /> Add field
            </button>
          </div>

          {rawMaterials.length > 0 && setRawMaterialAssignment && (
            <div style={{ marginTop: "24px", padding: "16px", background: "#F0FDF4", borderRadius: "12px" }}>
              <h4 style={{ marginBottom: "12px", fontSize: "14px", fontWeight: 600 }}>Raw Material Selection for Product</h4>
              <div className="form-grid">
                <div className="form-group">
                  <label>Select Raw Material Type</label>
                  <select
                    value={assignment.rawMaterialId}
                    onChange={(e) => updateAssignmentField("rawMaterialId", e.target.value)}
                  >
                    <option value="">Select Raw Material</option>
                    {rawMaterials.map((rawMaterial) => (
                      <option key={rawMaterial.id} value={rawMaterial.id}>{rawMaterial.name} - {rawMaterial.type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Quantity</label>
                  <input
                    type="number"
                    value={assignment.quantity}
                    onChange={(e) => updateAssignmentField("quantity", parseInt(e.target.value, 10) || 1)}
                  />
                </div>
                <div className="form-group">
                  <label>Area (sq mm)</label>
                  <input
                    type="number"
                    value={assignment.area}
                    onChange={(e) => updateAssignmentField("area", parseFloat(e.target.value) || 0)}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-submit" onClick={onSubmit}>{submitLabel}</button>
        </div>
      </div>
    </div>
  );
}